import {
  clientCodeIssue,
  clientIndustrySchema,
  clientKindSchema,
  clientStatusSchema,
} from '@veem/schema'
import { z } from 'zod'

const id = z.string().trim().min(1).max(200)
const nullableText = z.string().trim().max(10_000).nullable()
const phone = z.string().trim().min(1).max(100).nullable()
const email = z.email().max(320).nullable()
const code = z
  .string()
  .regex(/^(\d{8}|\d{10})$/)
  .nullable()
const personFields = z
  .object({
    fullName: z.string().trim().min(1).max(300),
    phone,
    email,
  })
  .strict()
const contactFields = personFields
  .extend({
    role: z.string().trim().min(1).max(300),
    note: nullableText.optional(),
  })
  .strict()
const customerFields = z
  .object({
    kind: clientKindSchema,
    name: z.string().trim().min(1).max(300),
    edrpou: code,
    city: z.string().trim().min(1).max(300),
    phone,
    email,
    sellerEntityIds: z
      .array(id)
      .max(50)
      .refine((ids) => new Set(ids).size === ids.length),
    note: nullableText,
    categoryId: id.nullable().optional(),
  })
  .strict()

export const createCustomerSchema = customerFields
  .extend({
    contact: contactFields.nullable(),
    // Several people at once (director, accountant, manager); the first becomes primary.
    contacts: z.array(contactFields).max(100).optional(),
  })
  .strict()
  .superRefine((input, context) => {
    const message = clientCodeIssue(input.kind, input.edrpou)
    if (message) context.addIssue({ code: 'custom', path: ['edrpou'], message })
  })
export type CreateCustomerInput = z.infer<typeof createCustomerSchema>

const versioned = z.object({ id, version: z.number().int().positive() }).strict()
export const updateCustomerSchema = versioned
  .extend({
    patch: customerFields
      .extend({
        industry: clientIndustrySchema,
        status: clientStatusSchema,
        ownerId: id,
        address: nullableText,
        website: nullableText,
      })
      .partial()
      .strict()
      .refine((patch) => Object.keys(patch).length > 0),
  })
  .strict()
export type UpdateCustomerInput = z.infer<typeof updateCustomerSchema>

export const addContactSchema = versioned
  .extend({
    contact: contactFields.extend({ personId: id.optional() }).strict(),
  })
  .strict()
export type AddContactInput = z.infer<typeof addContactSchema>
export const updatePersonSchema = versioned
  .extend({
    patch: personFields
      .partial()
      .strict()
      .refine((patch) => Object.keys(patch).length > 0),
  })
  .strict()
export type UpdatePersonInput = z.infer<typeof updatePersonSchema>

export const setPrimaryContactSchema = versioned.extend({ personId: id }).strict()
export type SetPrimaryContactInput = z.infer<typeof setPrimaryContactSchema>
export const detachContactSchema = setPrimaryContactSchema
export type DetachContactInput = z.infer<typeof detachContactSchema>
export const updateLinkRoleSchema = setPrimaryContactSchema
  .extend({
    role: z.string().trim().min(1).max(300),
  })
  .strict()
export type UpdateLinkRoleInput = z.infer<typeof updateLinkRoleSchema>

export const customerCategoryCreateSchema = z
  .object({ name: id, parentId: id.nullable().optional() })
  .strict()
export type CustomerCategoryCreateInput = z.infer<typeof customerCategoryCreateSchema>
export const customerCategoryUpdateSchema = z
  .object({ version: z.number().int().positive(), name: id })
  .strict()
export const customerCategoryArchiveSchema = z
  .object({ version: z.number().int().positive(), archived: z.boolean() })
  .strict()
